'use client';

import React, { useState } from 'react';
import AppImage from '@/components/ui/AppImage';

export interface Property {
  id: number;
  name: string;
  location: string;
  country: string;
  type: string;
  rating: number;
  reviews: number;
  price: number;
  currency: string;
  images: { src: string; alt: string }[];
  badge?: string;
  isSuperhost?: boolean;
  dates?: string;
}

interface PropertyCardProps {
  property: Property;
}

export default function PropertyCard({ property }: PropertyCardProps) {
  const [liked, setLiked] = useState(false);
  const [imgIndex, setImgIndex] = useState(0);

  const prevImg = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setImgIndex((i) => (i === 0 ? property.images.length - 1 : i - 1));
  };

  const nextImg = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setImgIndex((i) => (i === property.images.length - 1 ? 0 : i + 1));
  };

  return (
    <a href={`/property/${property.id}`} className="group cursor-pointer block">
      {/* Image container */}
      <div className="relative aspect-square rounded-2xl overflow-hidden bg-gray-100 mb-3">
        <AppImage
          src={property.images[imgIndex].src}
          alt={property.images[imgIndex].alt}
          fill
          className="object-cover group-hover:scale-105 transition-transform duration-500"
        />

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

        {/* Badge */}
        {property.badge && (
          <div className="absolute top-3 left-3 bg-white text-gray-800 text-xs font-bold px-2.5 py-1 rounded-full shadow-sm">
            {property.badge}
          </div>
        )}

        {/* Wishlist button */}
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setLiked(!liked); }}
          className="absolute top-3 right-3 p-1.5 transition-transform hover:scale-110"
          aria-label={liked ? 'Remove from wishlist' : 'Add to wishlist'}
        >
          <svg
            className={`w-6 h-6 transition-colors drop-shadow-md ${liked ? 'text-[#4088C6] fill-[#4088C6]' : 'text-white fill-white/30 stroke-white'}`}
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
          </svg>
        </button>

        {/* Image navigation */}
        {property.images.length > 1 && (
          <>
            <button
              onClick={prevImg}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 bg-white/90 rounded-full flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
            >
              <svg className="w-3.5 h-3.5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={nextImg}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 bg-white/90 rounded-full flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
            >
              <svg className="w-3.5 h-3.5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            {/* Dots */}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {property.images.map((_, i) => (
                <div
                  key={i}
                  className={`w-1.5 h-1.5 rounded-full transition-colors ${i === imgIndex ? 'bg-white' : 'bg-white/50'}`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Card info */}
      <div>
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-0.5">
              {property.isSuperhost && (
                <span className="text-xs font-semibold text-gray-700 bg-gray-100 px-2 py-0.5 rounded-full">Superhost</span>
              )}
              <h3 className="text-sm font-semibold text-gray-900 truncate">{property.name}</h3>
            </div>
            <p className="text-sm text-gray-500 truncate">{property.location}, {property.country}</p>
            <p className="text-sm text-gray-500">{property.type}</p>
            {property.dates && <p className="text-sm text-gray-500">{property.dates}</p>}
          </div>
          {/* Rating */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <svg className="w-3.5 h-3.5 text-gray-900 fill-gray-900" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            <span className="text-sm font-semibold text-gray-900">{property.rating.toFixed(2)}</span>
          </div>
        </div>
        <div className="mt-1.5">
          <span className="text-sm font-bold text-gray-900">{property.currency} {property.price.toLocaleString()}</span>
          <span className="text-sm text-gray-500"> / night</span>
        </div>
      </div>
    </a>
  );
}
